import {formatDate} from '@angular/common';
import {inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subscription} from 'rxjs';
import {readCustomProperties, ThemeService} from '@shared/services/theme.service';

/**
 * The OCA-E-159 chart recipe for the Admin dashboard (ITP-E-147-02; mockup charts.ts:54-93): every chart takes its
 * colours from the --adb-* tokens of admin-dashboard/styles/_adb.scss, read off the chart's host element, and its
 * axis, legend, tooltip, zoom and grid shapes from the builders below. ECharts cannot read CSS custom properties, so
 * a chart calls adbChartTheme(element) once per render and hands ECharts real values.
 */
export interface AdbChartTheme {
  ink: string;
  ink2: string;
  ink3: string;
  line: string;
  lineSoft: string;
  surface: string;
  blue: string;
  blue2: string;
  green: string;
  greenBar: string;
  amber: string;
  red: string;
  purple: string;
  orange: string;
  font: string;
}

/** Light values; the fallback when a token is not resolvable (no element, a detached element, specs). */
export const ADB_CHART_LIGHT: AdbChartTheme = Object.freeze({
  ink: '#101214', ink2: '#596773', ink3: '#738496', line: '#DEE4EA', lineSoft: '#EAEEF3', surface: '#FFFFFF',
  blue: '#175CFF', blue2: '#5185FF', green: '#176448', greenBar: '#40B390', amber: '#7A4D0B', red: '#9B1942',
  purple: '#5C2693', orange: '#EE8349', font: 'Inter, sans-serif'
});

/** The custom property behind each colour of the theme. */
const TOKENS: ReadonlyArray<[Exclude<keyof AdbChartTheme, 'font'>, string]> = [
  ['ink', '--adb-ink'], ['ink2', '--adb-ink-2'], ['ink3', '--adb-ink-3'], ['line', '--adb-line'],
  ['lineSoft', '--adb-line-soft'], ['surface', '--adb-surface'], ['blue', '--adb-blue'], ['blue2', '--adb-blue-2'],
  ['green', '--adb-green'], ['greenBar', '--adb-green-bar'], ['amber', '--adb-amber'], ['red', '--adb-red'],
  ['purple', '--adb-purple'], ['orange', '--adb-orange']
];

/**
 * Reads the --adb-* tokens off `el` (the chart's host, which includes `adb.tokens` on its :host); each token that is
 * missing or empty falls back to ADB_CHART_LIGHT. `font` is the element's computed font-family. Always a new object.
 * The tokens are read through ThemeService's reader (GEN-E-137-08), so a chart drawn after a theme flip gets the
 * values of the theme now painted.
 */
export function adbChartTheme(el?: Element | null): AdbChartTheme {
  const theme: AdbChartTheme = {...ADB_CHART_LIGHT};
  if (!el || !el.isConnected) {
    return theme;
  }
  const values = readCustomProperties(el, TOKENS.map(([, name]) => name));
  for (const [key, name] of TOKENS) {
    if (values[name]) {
      theme[key] = values[name];
    }
  }
  const font = (getComputedStyle(el).fontFamily ?? '').trim();
  if (font) {
    theme.font = font;
  }
  return theme;
}

/**
 * Redraws a dashboard chart each time the painted theme flips (GEN-E-137-08): ECharts holds real colour values, so a
 * chart keeps the old theme's colours until its options are set again. Call it from a field initializer with the
 * chart's render; `redraw` re-reads adbChartTheme and calls setOption. Unsubscribes when the component is destroyed.
 */
export function adbRedrawOnThemeChange(redraw: () => void): Subscription {
  return inject(ThemeService).changes$.pipe(takeUntilDestroyed()).subscribe(() => redraw());
}

/** '#175CFF', 0.35 → 'rgba(23, 92, 255, 0.35)'. Accepts #RGB and #RRGGBB; anything else is returned unchanged. */
export function adbAlpha(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!match) {
    return hex;
  }
  const digits = match[1].length === 3 ? match[1].split('').map(d => d + d).join('') : match[1];
  const n = parseInt(digits, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** A category axis: a line in `line`, no ticks, 11 px labels in `ink2`. `extra` is merged last. */
export function adbCategoryAxis(t: AdbChartTheme, data: string[], extra: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    type: 'category',
    data,
    axisLine: {lineStyle: {color: t.line}},
    axisTick: {show: false},
    axisLabel: {color: t.ink2, fontSize: 11},
    ...extra
  };
}

/** A value axis: dashed split lines in `line`, no axis line, 11 px labels in `ink2`. `extra` is merged last. */
export function adbValueAxis(
  t: AdbChartTheme, formatter?: (v: number) => string, extra: Record<string, unknown> = {}
): Record<string, unknown> {
  return {
    type: 'value',
    splitLine: {lineStyle: {color: t.line, type: 'dashed'}},
    axisLine: {show: false},
    axisLabel: {color: t.ink2, fontSize: 11, ...(formatter ? {formatter} : {})},
    ...extra
  };
}

/** The legend: top right, 12×3 rounded swatches, 11 px labels in `ink2`. */
export function adbLegend(t: AdbChartTheme): Record<string, unknown> {
  return {top: 0, right: 0, icon: 'roundRect', itemWidth: 12, itemHeight: 3, textStyle: {color: t.ink2, fontSize: 11}};
}

/** The tooltip: an axis tooltip on the card surface with a 1 px `line` border and a soft shadow. */
export function adbTooltip(t: AdbChartTheme, extra: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    trigger: 'axis',
    backgroundColor: t.surface,
    borderColor: t.line,
    borderWidth: 1,
    padding: [8, 12],
    textStyle: {color: t.ink, fontSize: 12},
    extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,.12);border-radius:8px;',
    ...extra
  };
}

/** Zoom for a time series: wheel/drag inside the plot plus a 16 px slider 6 px from the bottom (grid bottom 52). */
export function adbZoom(t: AdbChartTheme): Record<string, unknown>[] {
  return [
    {type: 'inside', throttle: 50},
    {
      type: 'slider',
      height: 16,
      bottom: 6,
      borderColor: t.line,
      backgroundColor: 'transparent',
      fillerColor: adbAlpha(t.blue, 0.12),
      handleStyle: {color: t.surface, borderColor: t.ink2},
      moveHandleSize: 0,
      textStyle: {color: t.ink2, fontSize: 10},
      dataBackground: {lineStyle: {color: t.ink2, opacity: 0.4}, areaStyle: {color: t.line}},
      selectedDataBackground: {lineStyle: {color: t.blue}, areaStyle: {color: adbAlpha(t.blue, 0.15)}}
    }
  ];
}

/** The plot grid; a chart with adbZoom passes `{bottom: 52}`. */
export function adbGrid(extra: Record<string, unknown> = {}): Record<string, unknown> {
  return {left: 8, right: 8, top: 32, bottom: 8, containLabel: true, ...extra};
}

/**
 * The itemStyle of a partial point, today's "so far" value (ITP-E-211-01, Plan ITP-P-50 SPEC WEB-2): a 35% fill of the
 * colour inside a 1 px dashed border in the colour, so the point reads as unfinished. `t` is not read.
 */
export function adbPartialItem(t: AdbChartTheme, color: string): Record<string, unknown> {
  return {color: adbAlpha(color, 0.35), borderColor: color, borderType: 'dashed', borderWidth: 1};
}

/** '2026-10-08T14:20:41Z' → 'today so far (14:20 UTC)': what a partial point's tooltip says (ITP-E-211-01). */
export function adbTodaySoFar(asOfIso: string): string {
  return 'today so far (' + formatDate(asOfIso, 'HH:mm', 'en-US', 'UTC') + ' UTC)';
}

/**
 * A forecast on a category axis (ITP-E-214-01, Plan ITP-P-50 SPEC WEB-2): three line series, each `total` long and
 * null outside [offset, offset + points.length). `${name} band base` (low) and `${name} band` (high - low) share one
 * stack, so the band's fill (`color` at 15 %) lies between low and high; both are invisible lines and silent. `name`
 * is the forecast itself, a dashed 1.5 px line without symbols. A tooltip formatter reads the card's own data by
 * dataIndex, so the two band series never show up in a tooltip. `t` is kept for the pinned signature.
 */
export function adbForecastSeries(
  t: AdbChartTheme, name: string, color: string, offset: number,
  points: {value: number; low: number; high: number}[], total: number
): Record<string, unknown>[] {
  const at = (pick: (point: {value: number; low: number; high: number}) => number): (number | null)[] =>
    Array.from({length: total}, (_, i) => (i >= offset && i - offset < points.length ? pick(points[i - offset]) : null));
  return [
    {
      name: `${name} band base`, type: 'line', data: at(point => point.low), stack: `${name}-band`, symbol: 'none',
      lineStyle: {opacity: 0}, silent: true
    },
    {
      name: `${name} band`, type: 'line', data: at(point => point.high - point.low), stack: `${name}-band`, symbol: 'none',
      lineStyle: {opacity: 0}, areaStyle: {color: adbAlpha(color, 0.15)}, silent: true
    },
    {
      name, type: 'line', data: at(point => point.value), symbol: 'none',
      lineStyle: {type: 'dashed', width: 1.5, color}, itemStyle: {color}
    }
  ];
}
